import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@vnetwork/ui/feedback';
import {
  currentWorkspaceRoute,
  iamProjectStatus,
  organizationNameOf,
  nextOpenWorkspace,
  openMachineFrom,
  organizationStillReachable,
  type OpenWorkspaceRecord,
  projectNameMap,
  planBack,
  planRevoked,
  planSwitch,
  sameId,
  useOrgCustomerId,
  useIamProjects,
  useOrgWorkspaces,
  useSharedProjects,
  useWorkingProject,
  type StoredProject,
} from '@/api/iam';
import { onKeys } from '@/api/opennebula/_shared';
import type { Instance } from '@vnetwork/margarita-shell/opennebula/instances/instance-types';
import { WORKSPACE_SERVICES } from '@/lib/workspace-services';
import { workspaceLocation } from '../home-org-note';
import { createLaggedRefetch, refetchInstanceLists } from './refresh-instances';
import { onUnresolvedConsole, rememberChoice, revokedToast, switchedText, type KnownChoice } from './model';
import { NOTICE_TOAST_MS } from '@vnetwork/margarita-shell/toast-options';
import { useAuth } from '@/auth/AuthProvider';

/** The services whose pages the project choice may move; the others' sources are not fetched by the top bar. */
const SWITCHABLE = WORKSPACE_SERVICES.filter((s) => s.switchable);

/** The workspaces of the switchable services, and whether they have all answered (a failed answer counts). */
function useSwitchableWorkspaces() {
  return useOrgWorkspaces(SWITCHABLE);
}

/**
 * Storing the choice and moving the page with it. Choosing a project stores it, and when the open page is inside a
 * workspace of another organization the page follows (`planSwitch`, history replace, a toast). `back` is set only while
 * a foreign workspace is open: it stores "all projects" and goes to the same page type in the home workspace, or to the
 * service's landing when there is none (`planBack`).
 */
export function useApplyProjectChoice() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // The registry reads the workspace from the path (Compute) or the search (Kubernetes).
  const loc = workspaceLocation(pathname, search);
  const toast = useToast();
  const home = useOrgCustomerId();
  const { choose } = useWorkingProject();
  const { workspaces, settled } = useSwitchableWorkspaces();
  const { data: sharedProjects = [] } = useSharedProjects();

  const foreign = useMemo(() => {
    const current = currentWorkspaceRoute(SWITCHABLE, loc, workspaces);
    return current && home && !sameId(current.match.organizationId, home) ? current : null;
  }, [loc, workspaces, home]);

  const apply = useCallback(
    (next: StoredProject | null, label: string) => {
      choose(next);
      if (!next) return;
      const plan = planSwitch({ registry: SWITCHABLE, loc, choice: { organizationId: next.organizationId }, workspaces });
      if (plan.kind !== 'navigate') return;
      navigate(plan.to, { replace: true });
      toast.info(switchedText(label, plan.toList), { duration: NOTICE_TOAST_MS });
    },
    [choose, loc, workspaces, navigate, toast],
  );

  const back = useMemo(
    () =>
      foreign
        ? () => {
            choose(null);
            const plan = planBack({ registry: SWITCHABLE, loc, workspaces });
            if (plan.kind !== 'navigate') return;
            navigate(plan.to, { replace: true });
            toast.info(switchedText('your organization', plan.toList), { duration: NOTICE_TOAST_MS });
          }
        : null,
    [foreign, choose, loc, workspaces, navigate, toast],
  );

  const unresolvedConsole = useMemo(
    () => onUnresolvedConsole({ loc, inWorkspace: currentWorkspaceRoute(SWITCHABLE, loc, workspaces) !== null, settled }),
    [loc, workspaces, settled],
  );

  // The organization whose workspace is open (any service), for the label when no project of it is chosen.
  const foreignOrgName = foreign
    ? organizationNameOf(
        foreign.match.organizationId,
        sharedProjects,
        workspaces.find((w) => sameId(w.organizationId, foreign.match.organizationId))?.organizationName,
      )
    : undefined;

  return { apply, back, workspacesSettled: settled, foreignOrgName, unresolvedConsole };
}

/**
 * A stored choice that is no longer usable (`revoked`): `planRevoked` says whether to clear it and whether to leave the
 * workspace of that choice's organization for the service's landing, which also says so once (E4).
 */
export function useRevokedChoice(revoked: boolean, stored: StoredProject | null, clear: () => void) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const loc = workspaceLocation(pathname, search);
  const toast = useToast();
  const qc = useQueryClient();
  const lagged = useMemo(() => createLaggedRefetch(qc), [qc]);
  const { user } = useAuth();
  const { workspaces, settled, failed } = useSwitchableWorkspaces();
  const home = useOrgCustomerId();
  const { data: own = [] } = useIamProjects(undefined, false, { enabled: true });
  const { data: shared = [] } = useSharedProjects();
  const organizationReachable = organizationStillReachable({
    organizationId: stored?.organizationId ?? '',
    home,
    own: own.filter((p) => iamProjectStatus(p) === 'active'),
    shared,
  });
  const orgName = organizationNameOf(stored?.organizationId, shared, workspaces.find((w) => sameId(w.organizationId, stored?.organizationId))?.organizationName);

  // The lists forget the project, its organization and the workspace the page was in at the very moment access ends: keep what they said before.
  const known = useRef<KnownChoice | null>(null);
  const wasOpen = useRef<OpenWorkspaceRecord | null>(null);
  const names = useMemo(() => projectNameMap(own, shared), [own, shared]);
  const current = currentWorkspaceRoute(SWITCHABLE, loc, workspaces);
  if (!revoked) known.current = rememberChoice(known.current, { stored, projectName: stored ? names.get(stored.projectId) : undefined, orgName });
  const currentKey = current ? `${current.service.type}|${current.match.organizationId}` : '';
  // The open machine's project comes from the detail page's own query: observed here (never enabled, never fetched from here).
  const resource = current?.match.resource;
  const detail = useQuery<Instance>({ queryKey: onKeys.instance(resource?.serviceId ?? '', resource?.id ?? ''), queryFn: skipToken });
  const hasMachine = !!resource;
  // Memoised on what it is made of: a new object every render would re-run the revoke effect (and its toast) every render.
  const openMachine = useMemo(
    () => (hasMachine ? openMachineFrom({ isPending: detail.isPending, isError: detail.isError, error: detail.error, data: detail.data }) : undefined),
    [hasMachine, detail.isPending, detail.isError, detail.error, detail.data],
  );
  // Declared before the revoke effect: both run after the same render, and the revoke must see this render's record.
  useEffect(() => {
    wasOpen.current = nextOpenWorkspace(wasOpen.current, {
      current: current ? { serviceType: current.service.type, organizationId: current.match.organizationId } : null,
      pathname,
    });
  }, [currentKey, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // The late refetch ends with the page, and with a new choice of project (it was for the old one).
  useEffect(() => lagged.cancel, [lagged]);
  useEffect(() => {
    if (stored) lagged.cancel();
  }, [stored, lagged]);

  useEffect(() => {
    const plan = planRevoked({ registry: SWITCHABLE, loc, revoked, stored, organizationReachable, workspaces, settled, failed, wasOpen: wasOpen.current, openMachine });
    const text = revokedToast({ plan, organizationName: orgName, organizationReachable, known: known.current, personName: user?.name });
    if (plan.redirect) navigate(plan.redirect, { replace: true });
    if (text) toast.info(text, { duration: NOTICE_TOAST_MS });
    if (plan.clear) {
      clear();
      refetchInstanceLists(qc);
      lagged.schedule();
    }
  }, [revoked, stored, organizationReachable, loc, workspaces, settled, failed, openMachine, navigate, toast, clear, qc, lagged, orgName, user?.name]);
}
